<script>
export default {
  name: 'UhModal',
};
</script>

<script setup>
const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  persistent: {
    type: Boolean,
    default: false,
  },
  showDefaultCloseBtn: {
    type: Boolean,
    default: true,
  },
  minHeight: {
    type: String,
    default: '400px',
  },
  maxHeight: {
    type: String,
    default: '100%',
  },
  width: {
    type: String,
    default: 'fit-content',
  },
  maxWidth: {
    type: String,
    default: '100%',
  },
  height: {
    type: String,
    default: 'fit-content',
  },
  modalClasses: {
    type: [String, Array],
    default: '',
  },
  headerClasses: {
    type: String,
    default: '',
  },
  bodyClasses: {
    type: String,
    default: '',
  },
  footerClasses: {
    type: String,
    default: '',
  },
  blur: {
    type: Boolean,
    default: true,
  },
  dusk: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['close']);

const onClickOutside = () => {
  if (!props.persistent) emit('close');
};
</script>

<template>
  <MountingPortal mount-to="#PortalMountTo" append>
    <div>
      <Transition name="uni-fade" appear>
        <div
          v-if="show"
          class="modal-overlay"
          :class="{ 'tw-backdrop-blur-[2px]': props.blur }"
          @click="onClickOutside"
        ></div>
      </Transition>
      <Transition name="uni-pop" appear>
        <div
          v-if="show"
          class="modal"
          :class="modalClasses"
          :style="{ minHeight, maxHeight, width, height, maxWidth }"
          :dusk="dusk"
          v-bind="$attrs"
        >
          <header
            v-if="$slots.header"
            :class="headerClasses"
            class="modal-header tw-p-[18px]"
            :dusk="`${dusk}-header`"
            :data-testid="`${dusk}-header`"
          >
            <slot name="header"></slot>
            <button
              v-show="showDefaultCloseBtn"
              type="button"
              class="btn-close"
              :data-testid="`${dusk}-close`"
              @click="$emit('close')"
            >
              <i class="fas fa-times"></i>
            </button>
          </header>

          <section
            :class="bodyClasses"
            class="modal-body"
            :dusk="`${dusk}-body`"
            :data-testid="`${dusk}-body`"
          >
            <slot name="body"></slot>
          </section>

          <footer
            v-if="$slots.footer"
            :class="footerClasses"
            class="modal-footer"
            :dusk="`${dusk}-footer`"
            :data-testid="`${dusk}-footer`"
          >
            <slot name="footer"></slot>
          </footer>
        </div>
      </Transition>
    </div>
  </MountingPortal>
</template>

<style>
/* ---------------------------------- */
.uni-fade-enter-active,
.uni-fade-leave-active {
  transition: opacity 0.25s linear;
}

.uni-fade-enter,
.uni-fade-leave-to {
  opacity: 0;
}

.uni-pop-enter-active,
.uni-pop-leave-active {
  transition: transform 0.25s cubic-bezier(0.5, 0, 0.5, 1), opacity 0.25s linear;
}

.uni-pop-enter,
.uni-pop-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(10%);
  transition: transform 0.15s cubic-bezier(0.5, 0, 0.5, 1), opacity 0.15s linear;
}
</style>

<style scoped lang="scss">
.modal {
  position: fixed;
  inset: 0;
  margin: auto;

  display: flex;
  flex-direction: column;
  align-items: stretch;

  max-height: 90vh;
  max-width: 100%;
  height: auto;

  background: #ffffff;
  border-radius: 0.5em;
  box-shadow: rgba(0, 0, 0, 0.19) 0px 10px 20px, rgba(0, 0, 0, 0.23) 0px 6px 6px;

  z-index: 1010;
  overflow: hidden;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 1000;
  -webkit-backdrop-filter: blur(2px);
  backdrop-filter: blur(2px);
}

/* Prevent Safari rendering glitches */
@supports (-webkit-touch-callout: none) {
  .modal {
    transform: translateZ(0);
  }
}

.modal-header,
.modal-footer {
  padding: 1em;
  display: flex;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;

  width: 100%; /* keeps border and layout consistent */
  flex-shrink: 0; /* Safari fix */
  border-bottom: 1px solid #eee;
}

.modal-body {
  width: 100%;
  /*height: 100%;*/
  padding: 1em;
}

.modal-footer {
  flex-direction: column;
  justify-content: flex-end;
}

.btn-close {
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  font-weight: bold;
  color: #999999;
  background: transparent;
}

.modal-header .btn-close {
  margin-left: auto;
  flex-shrink: 0;
}

.btn-green {
  color: white;
  background: #4aae9b;
  border: 1px solid #4aae9b;
  border-radius: 2px;
}

.mobile-filter-refresh .btn-close {
  display: none;
}
</style>
